import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import {
  Description as AllOrdersIcon,
  HeadsetMic as DeliveryIcon,
  PointOfSale as CashDeskIcon,
  TableRestaurant as RestaurantIcon,
  Restaurant as KitchenIcon,
  DirectionsCar as DriverIcon,
  BarChart as ReportsIcon,
  Tune as SettingsIcon,
  PersonOutline as PersonIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';

const ADMIN_EMAILS = ['admin@saheehpos.com', 'owner@saheehpos.com'];

interface Tile {
  key: string;
  label: string;
  labelAr: string;
  icon: React.ReactNode;
  path: string;
  adminOnly?: boolean;
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [userName, setUserName] = useState<string>('');

  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('saheeh_pos_user') || '{}');
      setUserName(u.name || u.email || '');
    } catch {}
    try {
      const s = JSON.parse(localStorage.getItem('saheeh_pos_session') || '{}');
      if (!userName && s.user?.email) setUserName(s.user.name || s.user.email || '');
    } catch {}
  }, []);

  const getEmail = (): string => {
    try {
      const u = JSON.parse(localStorage.getItem('saheeh_pos_user') || '{}');
      if (u.email) return u.email.toLowerCase();
    } catch {}
    try {
      const s = JSON.parse(localStorage.getItem('saheeh_pos_session') || '{}');
      if (s.user?.email) return s.user.email.toLowerCase();
    } catch {}
    return '';
  };
  const isAdmin = ADMIN_EMAILS.includes(getEmail());

  const iconSx = { fontSize: { xs: 32, md: 38 }, color: '#94e815' };

  const allTiles: Tile[] = [
    { key: 'orders',   label: 'All orders',     labelAr: 'جميع الطلبات',    icon: <AllOrdersIcon sx={iconSx} />,  path: '/orders' },
    { key: 'delivery', label: 'Delivery service', labelAr: 'خدمة التوصيل',  icon: <DeliveryIcon sx={iconSx} />,   path: '/delivery' },
    { key: 'cash',     label: 'Cash desk',       labelAr: 'الكاشير',         icon: <CashDeskIcon sx={iconSx} />,   path: '/cashdesk' },
    { key: 'tables',   label: 'Restaurant',      labelAr: 'المطعم',          icon: <RestaurantIcon sx={iconSx} />, path: '/restaurant' },
    { key: 'kitchen',  label: 'Kitchen screen',  labelAr: 'شاشة المطبخ',     icon: <KitchenIcon sx={iconSx} />,    path: '/kitchen' },
    { key: 'drivers',  label: 'Driver screen',   labelAr: 'شاشة السائق',     icon: <DriverIcon sx={iconSx} />,     path: '/drivers' },
    { key: 'reports',  label: 'Reports',         labelAr: 'التقارير',        icon: <ReportsIcon sx={iconSx} />,    path: '/daily-closing' },
    { key: 'settings', label: 'Settings',        labelAr: 'الإعدادات',       icon: <SettingsIcon sx={iconSx} />,   path: '/settings', adminOnly: true },
  ];

  const tiles = allTiles.filter(t => !t.adminOnly || isAdmin);

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, pb: { xs: 3, md: 2.5 }, maxWidth: 1400, mx: 'auto' }}>
      {/* User banner */}
      {userName && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            mb: { xs: 1.5, md: 2 },
            p: { xs: 1.25, md: 1.5 },
            borderRadius: 2,
            border: '1px solid rgba(148,232,21,0.35)',
            bgcolor: 'rgba(148,232,21,0.08)',
          }}
        >
          <PersonIcon sx={{ color: '#94e815', fontSize: { xs: 26, md: 30 } }} />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#9aa0b5', fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              {isAr ? 'مسجّل دخول بـ' : 'Signed in as'}
            </Typography>
            <Typography sx={{ fontSize: { xs: '1.05rem', md: '1.2rem' }, fontWeight: 800, color: '#94e815', lineHeight: 1.1 }}>
              {userName}
            </Typography>
          </Box>
        </Box>
      )}

      <Typography
        sx={{
          fontSize: { xs: '1.2rem', md: '1.4rem' },
          fontWeight: 700,
          color: '#FFFFFF',
          mb: { xs: 1.5, md: 2 },
          pl: 0.5,
        }}
      >
        {isAr ? 'الوصول السريع' : 'Fast access'}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(4, 1fr)',
            lg: 'repeat(5, 1fr)',
          },
          gap: { xs: 1.2, md: 1.5 },
        }}
      >
        {tiles.map(tile => (
          <Box
            key={tile.key}
            onClick={() => navigate(tile.path)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 0.8, md: 1 },
              py: { xs: 1.8, md: 2 },
              px: 1.2,
              borderRadius: '14px',
              backgroundColor: '#252838',
              border: '1.5px solid #3a3d52',
              cursor: 'pointer',
              minHeight: { xs: 92, md: 108 },
              transition: 'all 0.18s ease',
              '&:hover': {
                borderColor: '#94e815',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 18px rgba(148,232,21,0.20)',
              },
            }}
          >
            {tile.icon}
            <Typography
              sx={{
                fontSize: { xs: '0.82rem', md: '0.92rem' },
                fontWeight: 600,
                color: '#FFFFFF',
                textAlign: 'center',
                lineHeight: 1.2,
              }}
            >
              {isAr ? tile.labelAr : tile.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default HomePage;
